import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BookOpen, PlayCircle, ClipboardCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { StudentShell } from "../components/StudentShell";
import { useLang } from "../i18n";
import { api, API, useAuth } from "../api";
import { gradeProfile } from "../gradeProfile";
import { imageUrl } from "../components/BlocksEditor";
import { ReadingCard } from "../components/ReadingCard";
import { sfx } from "../sfx";

const embed = (url) => {
  const m = String(url).match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
};

const VideoPlayer = ({ videoId, className }) => {
  const token = localStorage.getItem("el_token");
  return <video src={`${API}/videos/${videoId}/stream?auth=${token}`} controls preload="metadata"
    className={className} data-testid="topic-video-file" />;
};

export default function Topics() {
  const { t } = useLang();
  const { user } = useAuth();
  const profile = gradeProfile(user?.grade);
  const [units, setUnits] = useState([]);
  const [topics, setTopics] = useState([]);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [exam, setExam] = useState(null);
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);

  const startUnitExam = async (unitName, count = 8) => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/unit-exam", { unit: unitName, count });
      if (!data.questions?.length) throw new Error("empty");
      setExam(data); setI(0); setPicked(null); setScore(0);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذر توليد اختبار الوحدة"); }
    finally { setBusy(false); }
  };

  const answer = (k) => {
    if (picked !== null) return;
    setPicked(k);
    const ok = k === exam.questions[i].answer;
    if (ok) { setScore((s) => s + 1); sfx.correct(); toast.success(t("correct")); }
    else { sfx.wrong(); toast.error(t("wrong")); }
  };

  const nextQ = async () => {
    if (i + 1 < exam.questions.length) { setI(i + 1); setPicked(null); return; }
    sfx.points();
    await api.post("/ai/quiz/result", { topic: exam.topic, score, total: exam.questions.length })
      .catch(() => {});
    toast.success(`${t("yourScore")}: ${score}/${exam.questions.length}`);
    setExam(null);
  };

  useEffect(() => {
    api.get("/topics").then((r) => { setTopics(r.data); setActive(r.data[0] || null); });
    api.get("/units").then((r) => setUnits(r.data)).catch(() => setUnits([]));
  }, []);

  const list = useMemo(() => topics.filter((tp) =>
    tp.title_en.toLowerCase().includes(q.toLowerCase()) || tp.title_ar.includes(q)), [topics, q]);

  return (
    <StudentShell>
      {exam && (
        <div className="fixed inset-0 z-[60] bg-[#04102a]/85 backdrop-blur-md overflow-auto p-4"
          data-testid="unit-exam-modal">
          <div className="glass max-w-2xl mx-auto mt-10 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black text-white">{exam.topic}</h2>
              <button onClick={() => setExam(null)} data-testid="unit-exam-close"
                className="pro-btn bg-white/12 text-white text-sm">{t("back")}</button>
            </div>
            <div className="text-xs text-white/60 mt-1">{i + 1}/{exam.questions.length} · ⭐ {score}</div>
            <h3 className="text-2xl font-black text-white mt-5" dir="ltr">{exam.questions[i].question}</h3>
            <div className="space-y-3 mt-5">
              {(exam.questions[i].options || []).map((o, k) => {
                const isRight = k === exam.questions[i].answer;
                const bg = picked === null ? "rgba(255,255,255,.1)"
                  : isRight ? "#2fa84f" : picked === k ? "#d92b45" : "rgba(255,255,255,.1)";
                return (
                  <button key={k} onClick={() => answer(k)} data-testid={`unit-exam-option-${k}`} dir="ltr"
                    className="w-full text-start rounded-2xl border-2 px-5 py-4 font-bold text-white"
                    style={{ background: bg, borderColor: "rgba(255,255,255,.2)" }}>{o}</button>
                );
              })}
            </div>
            {picked !== null && (
              <>
                <p className="text-sm text-white/70 mt-4">{exam.questions[i].explanation_ar}</p>
                <button onClick={nextQ} data-testid="unit-exam-next"
                  className="play-btn text-white w-full mt-4" style={{ background: "var(--play-primary)" }}>
                  {i + 1 === exam.questions.length ? t("finish") : t("next")}</button>
              </>
            )}
          </div>
        </div>
      )}
      <h1 className="text-4xl font-black text-white glow-text">{t("topics")}</h1>
      <p className="text-white/65 mt-2">{profile.label.ar} · {t("grade")} {profile.grade} · {profile.book} · {topics.length} {t("lesson")}</p>
      <input className="input mt-4 max-w-sm" placeholder={t("search")} value={q}
        onChange={(e) => setQ(e.target.value)} data-testid="topics-search" />

      <div className="grid md:grid-cols-3 gap-6 mt-8">
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pe-1">
          {list.map((tp) => (
            <button key={tp.id} data-testid={`topic-item-${tp.id}`}
              onClick={(e) => {
                setActive(tp);
                if (window.innerWidth < 1024) {
                  setTimeout(() => document.getElementById("topic-detail")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
                }
              }}
              className="w-full text-start play-card py-4"
              style={{ borderColor: active?.id === tp.id ? "var(--play-primary)" : "#E2E8F0" }}>
              <div className="font-extrabold">{tp.title_ar}</div>
              <div className="text-sm text-white/65">{tp.title_en}</div>
            </button>
          ))}
          {!list.length && <div className="play-card text-white/45">{t("noData")}</div>}
        </div>

        {active && (
          <motion.div key={active.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="md:col-span-2 play-card" id="topic-detail" data-testid="topic-detail">
            <h2 className="text-3xl font-black">{active.title_ar}</h2>
            <div className="text-lg font-bold text-white/65">{active.title_en}</div>

            {(active.blocks || []).length > 0 ? (
              <div className="space-y-5 mt-6" data-testid="topic-blocks">
                {active.blocks.map((b, k) => {
                  if (b.type === "heading") return (
                    <h3 key={k} className="text-2xl font-black" dir="auto"
                      data-testid={`block-view-heading-${k}`}>{b.text}</h3>);
                  if (b.type === "tip") return (
                    <div key={k} className="glass-soft p-4 text-sm leading-relaxed"
                      data-testid={`block-view-tip-${k}`}>💡 {b.text}</div>);
                  if (b.type === "image") return b.image_id ? (
                    <figure key={k} data-testid={`block-view-image-${k}`}>
                      <img src={imageUrl(b.image_id)} alt={b.text || ""}
                        className="max-w-full max-h-96 mx-auto rounded-2xl object-contain" />
                      {b.text && <figcaption className="text-xs text-white/55 mt-2 text-center">{b.text}</figcaption>}
                    </figure>) : null;
                  if (b.type === "video") return b.video_id ? (
                    <div key={k} className="aspect-video" data-testid={`block-view-video-${k}`}>
                      <VideoPlayer videoId={b.video_id} className="w-full h-full rounded-2xl bg-black" />
                    </div>) : null;
                  if (b.type === "words") return (
                    <div key={k} className="grid sm:grid-cols-3 gap-3" data-testid={`block-view-words-${k}`}>
                      {(b.words || []).map((w) => (
                        <div key={w} dir="ltr"
                          className="rounded-xl border-2 border-white/10 px-3 py-2 text-center font-bold">{w}</div>
                      ))}
                    </div>);
                  return (
                    <p key={k} className="leading-loose text-white/85" dir="auto"
                      data-testid={`block-view-text-${k}`}>{b.text}</p>);
                })}
              </div>
            ) : (
            <>
            <p className="mt-4 leading-loose">{active.summary_ar}</p>
            <p className="mt-4 leading-relaxed text-white/80" dir="ltr">{active.content_en}</p>

            {active.video_id ? (
              <div className="aspect-video mt-6">
                <VideoPlayer videoId={active.video_id} className="w-full h-full rounded-2xl bg-black" />
              </div>
            ) : active.video_url ? (
              <div className="aspect-video mt-6">
                <iframe title="video" src={embed(active.video_url)} className="w-full h-full rounded-2xl"
                  allowFullScreen data-testid="topic-video" />
              </div>
            ) : (
              <div className="mt-6 rounded-2xl bg-white/10 p-6 text-center text-white/45 flex flex-col items-center gap-2">
                <PlayCircle size={26} />لا يوجد فيديو لهذا الدرس بعد
              </div>
            )}

            <h3 className="text-xl font-black mt-8">{t("words")}</h3>
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              {(active.words || []).map((w) => (
                <div key={w} className="rounded-xl border-2 border-white/10 px-3 py-2 text-center font-bold"
                  dir="ltr" data-testid={`word-${w}`}>{w}</div>
              ))}
            </div>
            </>
            )}
            <Link to="/student/dictionary" data-testid="go-dictionary-btn"
              className="play-btn text-white inline-flex items-center gap-2 mt-5 text-sm"
              style={{ background: "var(--play-secondary)" }}>
              <BookOpen size={16} />{t("dictionary")}</Link>

            <ReadingCard topic={active} />

            {(() => {
              const unitName = active.unit || active.title_en.split(" - Part")[0];
              const unit = units.find((u) => u.unit === unitName);
              if (!unit) return null;
              return (
                <div className="glass-soft mt-6 p-4" data-testid="unit-exam-card">
                  <div className="font-extrabold">{unit.unit_ar || unit.unit}</div>
                  <div className="text-xs text-white/60 mt-1">
                    {unit.parts > 1 ? `${unit.parts} أجزاء` : "درس واحد"} · {unit.words.length} كلمة
                    {unit.parts > 1 ? " — اختبار نهاية الوحدة" : " — اختبار الدرس"}</div>
                  <button data-testid="unit-exam-btn" disabled={busy}
                    onClick={() => startUnitExam(unitName, Math.max(4, Math.min(8, Math.floor(unit.words.length / 3))))}
                    className="play-btn text-white mt-3 text-sm inline-flex items-center gap-2 disabled:opacity-60"
                    style={{ background: "var(--play-accent)" }}>
                    <ClipboardCheck size={15} />{busy ? t("loading") : "ابدأ اختبار الوحدة"}</button>
                </div>
              );
            })()}
          </motion.div>
        )}
      </div>
    </StudentShell>
  );
}
